import { useTranslation } from "react-i18next";
import { CalendarDots, CheckCircle, Clock, HourglassMedium } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { CalendarSession } from "./types";
import { effectiveStatus, parseTime } from "./types";

interface CalendarSummaryProps {
  sessions: CalendarSession[];
  selectedSessions: CalendarSession[];
  selectedDate: Date | null;
  isLoading?: boolean;
}

export function CalendarSummary({
  sessions,
  selectedSessions,
  selectedDate,
  isLoading = false,
}: CalendarSummaryProps) {
  const { t } = useTranslation();
  const { statusCounts, periodCount, totalMinutes, nextSession } = summarizeCalendar(sessions);

  return (
    <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryTile
        icon={CalendarDots}
        label={t("calendar.periodSessions")}
        value={isLoading ? "..." : String(periodCount)}
        hint={t("calendar.periodMinutes", { count: totalMinutes })}
      />
      <SummaryTile
        icon={Clock}
        label={selectedDate ? t("calendar.selectedDay") : t("calendar.selectDay")}
        value={isLoading ? "..." : String(selectedSessions.length)}
        hint={selectedDate ? t("calendar.sessionsForDay") : t("calendar.clickDay")}
      />
      <SummaryTile
        icon={HourglassMedium}
        label={t("calendar.activeSessions")}
        value={isLoading ? "..." : String(statusCounts.scheduled + statusCounts.hold)}
        hint={t("calendar.holdCount", { count: statusCounts.hold })}
        accent={statusCounts.hold > 0}
      />
      <SummaryTile
        icon={CheckCircle}
        label={nextSession ? t("calendar.nextSession") : t("calendar.completed")}
        value={nextSession ? formatTime(nextSession.scheduledAt) : String(statusCounts.completed)}
        hint={nextSession ? nextSession.clientName : t("calendar.completedInPeriod")}
      />
    </section>
  );
}

// Cancelled / no-show sessions aren't time spent, so they count toward neither
// the period total nor its minutes, and only a still-scheduled session can be
// the next one.
const isCountable = (s: CalendarSession) => s.status !== "cancelled" && s.status !== "no_show";

export function summarizeCalendar(sessions: CalendarSession[], nowMs: number = Date.now()) {
  const statusCounts = { scheduled: 0, hold: 0, completed: 0 };
  let periodCount = 0;
  let totalMinutes = 0;
  for (const session of sessions) {
    const status = effectiveStatus(session, nowMs);
    if (status === "completed") statusCounts.completed += 1;
    else if (status === "hold") statusCounts.hold += 1;
    else if (status === "scheduled") statusCounts.scheduled += 1;
    if (isCountable(session)) {
      periodCount += 1;
      totalMinutes += session.durationMinutes;
    }
  }
  const nextSession = sessions
    .filter((s) => s.status === "scheduled" && Date.parse(s.scheduledAt) > nowMs)
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))[0];
  return { statusCounts, periodCount, totalMinutes, nextSession };
}

function SummaryTile({
  icon: Icon,
  label,
  value,
  hint,
  accent = false,
}: {
  icon: typeof CalendarDots;
  label: string;
  value: string;
  hint: string;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-border/60 bg-card px-4 py-3 shadow-sm",
        accent && "border-amber-200 bg-amber-50/60",
      )}
    >
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon className={cn("size-4", accent && "text-amber-700")} />
        <span>{label}</span>
      </div>
      <p className="mt-1.5 text-2xl font-bold tabular-nums text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
        {value}
      </p>
      <p className="truncate text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function formatTime(iso: string): string {
  const time = parseTime(iso);
  return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}`;
}
